import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Leaf, Plus, MapPin, Ruler, Trash2, Eye, Calendar,
  TreePine, Sun, Bug, Layers, Trophy, Sprout, Award, Check, Lock, Calculator, ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Challenge Progress Tracker Component
const ChallengeProgressTracker = ({ user }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadProgress();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API}/user/challenge-progress`, { withCredentials: true });
      setProgress(response.data);
    } catch (error) {
      console.error("Failed to load challenge progress:", error);
    } finally {
      setLoading(false);
    }
  };

  const colors = {
    green: { primary: "#2D8B4E", light: "#A8D5BA", dark: "#1A5632" },
    gold: { primary: "#FFB81C", light: "#FFE4A0" },
    plum: "#8B4A8C"
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];
  const monthKeys = ["january", "february", "march", "april", "may", "june",
                     "july", "august", "september", "october", "november", "december"];

  if (!user) {
    return (
      <Card className="mb-6 border-2" style={{ borderColor: colors.green.light }}>
        <CardContent className="p-6 text-center">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">Sign in to track your challenge progress</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6 text-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 bg-gray-200 rounded-full mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!progress) return null;

  return (
    <Card className="mb-6 overflow-hidden" style={{ background: `linear-gradient(135deg, white, ${colors.green.light}20)` }}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2" style={{ color: colors.green.dark }}>
            <Trophy className="w-5 h-5" style={{ color: colors.gold.primary }} />
            12-Month Challenge Progress
          </CardTitle>
          <Link to="/challenges">
            <Button variant="ghost" size="sm" style={{ color: colors.green.primary }}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-xl text-center" style={{ background: colors.green.light }}>
            <p className="text-2xl font-bold" style={{ color: colors.green.dark }}>{progress.summary.completed_months}/12</p>
            <p className="text-xs text-gray-600">Months Complete</p>
          </div>
          <div className="p-3 rounded-xl text-center" style={{ background: colors.gold.light }}>
            <p className="text-2xl font-bold" style={{ color: colors.gold.primary }}>{progress.summary.seeds_balance}</p>
            <p className="text-xs text-gray-600">Seeds Earned</p>
          </div>
          <div className="p-3 rounded-xl text-center" style={{ background: `${colors.plum}20` }}>
            <p className="text-2xl font-bold" style={{ color: colors.plum }}>{progress.summary.math_months_completed}</p>
            <p className="text-xs text-gray-600">Math Complete</p>
          </div>
          <div className="p-3 rounded-xl text-center bg-white border">
            <p className="text-sm font-bold" style={{ color: colors.green.dark }}>{progress.certificate.level}</p>
            <p className="text-xs text-gray-500">{progress.certificate.phase}</p>
          </div>
        </div>

        {/* Month Progress Grid */}
        <div className="grid grid-cols-6 md:grid-cols-12 gap-1">
          {monthKeys.map((month, i) => {
            const monthProgress = progress.monthly_progress[month];
            const isCompleted = monthProgress?.completed;
            const hasMath = monthProgress?.math_completed;
            
            return (
              <motion.div
                key={month}
                whileHover={{ scale: 1.1 }}
                className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs cursor-pointer ${
                  isCompleted ? '' : 'opacity-50'
                }`}
                style={{ 
                  background: isCompleted 
                    ? hasMath 
                      ? `linear-gradient(135deg, ${colors.green.primary}, ${colors.plum})` 
                      : colors.green.primary
                    : '#e5e7eb'
                }}
                title={`${monthNames[i]}: ${isCompleted ? 'Completed' : 'Not started'}${hasMath ? ' + Math' : ''}`}
              >
                {isCompleted ? (
                  <Check className="w-3 h-3 text-white" />
                ) : (
                  <Lock className="w-3 h-3 text-gray-400" />
                )}
                <span className={`text-[8px] mt-0.5 ${isCompleted ? 'text-white' : 'text-gray-400'}`}>
                  {monthNames[i].slice(0, 3)}
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* Achievements */}
        {progress.achievements.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {progress.achievements.map((achievement) => (
              <Badge 
                key={achievement.id}
                className="flex items-center gap-1"
                style={{ background: colors.gold.primary, color: colors.green.dark }}
              >
                <Award className="w-3 h-3" />
                {achievement.name}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SavedPlans = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/garden-plans`);
      setPlans(response.data);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      toast.error("Failed to load saved plans");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (planId) => {
    try {
      await axios.delete(`${API}/garden-plans/${planId}`);
      setPlans(plans.filter(p => p.id !== planId));
      toast.success("Garden plan deleted");
    } catch (error) {
      console.error("Failed to delete plan:", error);
      toast.error("Failed to delete plan");
    }
  };

  const getAestheticIcon = (aesthetic) => {
    const icons = {
      food: <Leaf className="w-4 h-4" />,
      serenity: <Sun className="w-4 h-4" />,
      wildlife: <Bug className="w-4 h-4" />,
      mixed: <TreePine className="w-4 h-4" />
    };
    return icons[aesthetic] || <Leaf className="w-4 h-4" />;
  };

  const getAestheticColor = (aesthetic) => {
    const colors = {
      food: "bg-green-100 text-green-700",
      serenity: "bg-blue-100 text-blue-700",
      wildlife: "bg-amber-100 text-amber-700",
      mixed: "bg-purple-100 text-purple-700"
    };
    return colors[aesthetic] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Leaf className="w-8 h-8 text-primary" />
            <span className="font-serif text-xl font-bold text-primary">ROOTSPACE</span>
          </Link>
          <Button 
            onClick={() => navigate('/create')}
            className="btn-primary"
            data-testid="create-plan-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Plan
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Challenge Progress Tracker */}
        <ChallengeProgressTracker user={user} />
        
        <div className="mb-10">
          <h1 className="font-serif text-4xl font-bold text-foreground mb-2">
            Your Garden Plans
          </h1>
          <p className="text-muted-foreground">
            View and manage your saved permaculture designs
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : plans.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
              <Layers className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="font-serif text-2xl font-semibold mb-2">No plans yet</h2>
            <p className="text-muted-foreground mb-6">
              Create your first garden plan to get started
            </p>
            <Button 
              onClick={() => navigate('/create')}
              className="btn-primary"
              data-testid="create-first-plan-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Plan
            </Button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className="card-organic h-full hover-lift"
                  data-testid={`plan-card-${index}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="font-serif text-xl mb-1">
                          {plan.name}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {plan.location}
                        </p>
                      </div>
                      <Badge className={`${getAestheticColor(plan.aesthetic)} flex items-center gap-1`}>
                        {getAestheticIcon(plan.aesthetic)}
                        <span className="capitalize">{plan.aesthetic}</span>
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Area</p>
                        <p className="font-mono font-semibold text-primary">
                          {(plan.width * plan.length).toFixed(0)} m²
                        </p>
                      </div>
                      <div className="text-center p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Plants</p>
                        <p className="font-mono font-semibold text-primary">
                          {plan.plant_recommendations?.length || 0}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Ruler className="w-4 h-4" />
                      <span>{plan.width}m × {plan.length}m × {plan.height}m</span>
                    </div>
                    
                    {plan.solar_data && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                        <Sun className="w-4 h-4 text-amber-500" />
                        <span>
                          {plan.solar_data.sun_hours_summer}h summer / {plan.solar_data.sun_hours_winter}h winter
                        </span>
                      </div>
                    )}
                  </CardContent>
                  
                  <CardFooter className="flex gap-2 pt-4 border-t border-border">
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/dashboard/${plan.id}`)}
                      data-testid={`view-plan-${index}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/calendar/${plan.id}`)}
                      data-testid={`calendar-plan-${index}`}
                    >
                      <Calendar className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-plan-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Garden Plan?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete &quot;{plan.name}&quot; and all associated reminders.
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(plan.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SavedPlans;
